import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import FilmCard from "@/components/feature/FilmCard";
import EmptyState, { CardGridSkeleton } from "@/components/base/EmptyState";
import FilterBar from "@/pages/category/components/FilterBar";
import { usePageTitle, useSimulatedLoading } from "@/hooks/usePageMeta";
import {
  defaultFilters,
  filterTitles,
  titles,
  type TitleFilters,
} from "@/utils/catalog";

const PAGE_SIZE = 20;

const typeMeta: Record<
  "movie" | "tv",
  { title: string; sub: string; desc: string; keyword: string }
> = {
  movie: {
    title: "电影分类",
    sub: "最新电影推荐",
    desc: "汇集院线新片、口碑佳作与影展作品，按类型、地区、年代自由筛选，快速找到想看的那一部。",
    keyword: "电影分类,最新电影",
  },
  tv: {
    title: "电视剧分类",
    sub: "热播电视剧",
    desc: "从悬疑刑侦到治愈生活，从国产剧到海外好剧，持续更新的剧集库与评分信息一目了然。",
    keyword: "电视剧分类,热播电视剧",
  },
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const kind = (type === "tv" ? "tv" : "movie") as "movie" | "tv";
  const meta = typeMeta[kind];

  const [filters, setFilters] = useState<TitleFilters>(defaultFilters);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const source = useMemo(() => titles.filter((t) => t.kind === kind), [kind]);
  const list = useMemo(() => filterTitles(source, filters), [source, filters]);
  const loading = useSimulatedLoading([kind, JSON.stringify(filters)], 320);

  usePageTitle(
    `${meta.title} - CC视频`,
    `${meta.desc}查看最新电影、热门电视剧，轻松筛选喜欢的影视作品。`,
  );

  const updateFilters = (next: Partial<TitleFilters>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setVisible(PAGE_SIZE);
  };

  const reset = () => {
    setFilters(defaultFilters);
    setVisible(PAGE_SIZE);
  };

  const shown = list.slice(0, visible);
  const hasMore = visible < list.length;

  return (
    <SiteLayout>
      {/* page header */}
      <section className="w-full bg-primary-950 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20cinematic%20header%20background%20of%20film%20reels%20and%20light%20streaks%20on%20a%20deep%20navy%20surface%20with%20subtle%20warm%20orange%20glow%2C%20minimal%20editorial%20texture%2C%20soft%20vignette%2C%20high%20contrast&width=1600&height=400&seq=cc-category-band&orientation=landscape"
          alt="分类页背景"
          title="CC视频影视分类"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-transparent" />
        <div className="relative z-10 w-full px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-1.5 text-xs text-background-400 mb-4 flex-wrap">
            <Link to="/" className="hover:text-accent-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
            <span className="text-background-300">分类</span>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
            <span className="text-accent-400">{kind === "movie" ? "电影" : "电视剧"}</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-black text-background-50 tracking-tight">
            {meta.sub}
          </h1>
          <p className="mt-3 text-sm text-background-300 leading-relaxed max-w-2xl">{meta.desc}</p>
          <div className="mt-5 flex flex-wrap items-center gap-2 text-[11px] text-background-300">
            <span className="px-2 py-1 rounded border border-background-50/20">
              共 {source.length} 部
            </span>
            <span className="px-2 py-1 rounded border border-background-50/20">
              默认每页 {PAGE_SIZE} 条
            </span>
            <span className="px-2 py-1 rounded bg-accent-500 text-primary-950">
              数据为演示内容
            </span>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 py-8 md:py-10">
        {/* mobile filter trigger */}
        <div className="lg:hidden mb-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileFilterOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 h-10 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 cursor-pointer"
          >
            <i className="ri-filter-3-line w-4 h-4 flex items-center justify-center" />
            筛选与排序
          </button>
          <MobileSortToggle filters={filters} onChange={updateFilters} />
        </div>

        <div className="hidden lg:block mb-6">
          <FilterBar
            filters={filters}
            onChange={updateFilters}
            onReset={reset}
            resultCount={list.length}
          />
        </div>

        {mobileFilterOpen && (
          <div className="fixed inset-0 z-[60] lg:hidden">
            <button
              type="button"
              aria-label="关闭筛选"
              onClick={() => setMobileFilterOpen(false)}
              className="absolute inset-0 bg-primary-950/50 cursor-pointer"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-xl max-h-[82vh] overflow-y-auto cc-fade-in">
              <div className="sticky top-0 flex items-center justify-between px-4 h-14 border-b border-background-200 bg-background-50">
                <span className="font-heading font-bold text-foreground-950">筛选与排序</span>
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen(false)}
                  className="w-9 h-9 flex items-center justify-center text-foreground-600 cursor-pointer"
                  aria-label="关闭"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>
              <div className="p-4">
                <FilterBar
                  filters={filters}
                  onChange={updateFilters}
                  onReset={reset}
                  resultCount={list.length}
                />
              </div>
              <div className="sticky bottom-0 p-4 bg-background-50 border-t border-background-200 flex gap-2">
                <button
                  type="button"
                  onClick={reset}
                  className="flex-1 h-11 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer"
                >
                  重置
                </button>
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen(false)}
                  className="flex-[2] h-11 rounded-md bg-accent-500 text-primary-950 text-sm font-medium cursor-pointer"
                >
                  查看 {list.length} 部作品
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <CardGridSkeleton count={10} />
        ) : list.length === 0 ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="暂无符合条件的影片"
            description="当前筛选条件下没有找到作品，试试放宽地区或年代，或者直接重置筛选。"
            actionLabel="重置筛选"
            onAction={reset}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
              {shown.map((item, i) => (
                <div key={item.id} className="cc-fade-up">
                  <FilmCard item={item} index={i} />
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center gap-3">
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="h-11 px-8 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  加载更多（还剩 {list.length - visible} 部）
                </button>
              ) : (
                <p className="text-xs text-foreground-400">
                  已经到底啦 · 共 {list.length} 部作品
                </p>
              )}
              <p className="text-xs text-foreground-400 font-label tabular-nums">
                已显示 {shown.length} / {list.length}
              </p>
            </div>
          </>
        )}
      </div>
    </SiteLayout>
  );
}

function MobileSortToggle({
  filters,
  onChange,
}: {
  filters: TitleFilters;
  onChange: (next: Partial<TitleFilters>) => void;
}) {
  const order: TitleFilters["sort"][] = ["latest", "rating", "popular"];
  const labels: Record<TitleFilters["sort"], string> = {
    latest: "上映时间",
    rating: "评分",
    popular: "热度",
  };
  const next = order[(order.indexOf(filters.sort) + 1) % order.length];
  return (
    <button
      type="button"
      onClick={() => onChange({ sort: next })}
      className="h-10 px-3 rounded-md border border-background-200 bg-background-50 text-xs text-foreground-700 whitespace-nowrap cursor-pointer"
    >
      <i className="ri-sort-desc mr-1" />
      {labels[filters.sort]}
    </button>
  );
}